import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { ClockCircleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Access,
  AccessSource,
  BundleAccess,
  EmployeeType,
  Subscription,
  SubscriptionAccess,
  SubscriptionAccessStatus,
  User,
} from '@prisma/client';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, Input, MenuProps, Space } from 'antd';
import Table from 'antd/es/table';

import { formatIsoDate } from '@app/utils-agnostic';
import { Tables } from '@app/web-components';

import { SubscriptionAccessStatusRenderer } from '../../admin/reports/access-status';
import { EmployeeTypeRenderer, UserPopover } from '../../common';
import { DetailModal as SubscriptionDetailModel } from '../../subscriptions/modals/detail';
import { CaseViewModal } from '../case-view';
import { accessCaseKeys, useAdvancedFindSubscriptionAccess } from '../data';

import { CaseRenderer } from './case-renderer';
import { EligibilityRenderer } from './eligibility-renderer';
import { StartPullUserSubscriptionAccessEligibilityButton } from './start-pull-user-subscription-access-eligibility-button';
import { SubscriptionAccessArchiveModal } from './subscription-access-archive-modal';

export type SubscriptionAccessTabProps = {
  bundleId: number;
  tableHeight: number;
};

export const SubscriptionAccessTab = ({ bundleId, tableHeight }: SubscriptionAccessTabProps) => {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState<{ subscriptionId: number } | undefined>();
  const [showCaseModal, setShowCaseModal] = useState<{ caseId: number } | undefined>();
  const [showSubscriptionAccessArchiveModal, setShowSubscriptionAccessArchiveModal] = useState<
    | { id: number; subscriberUserName: string; subscriptionAccessName: string; subscriptionAccessSource: AccessSource }
    | undefined
  >();

  type DataType = SubscriptionAccess & {
    subscription: Subscription & { user: User };
    bundleAccess: BundleAccess & { access: Access };
  };

  const actions: MenuProps['items'] = [
    {
      label: 'History',
      key: 'HISTORY',
      icon: <ClockCircleOutlined />,
    },
  ];

  const handleActionMenuClick = (e: { key: string }, record: DataType) => {
    switch (e.key) {
      case 'HISTORY':
        setShowSubscriptionAccessArchiveModal({
          id: record.id,
          subscriberUserName: record.subscription.user.name,
          subscriptionAccessName: record.bundleAccess.access.name,
          subscriptionAccessSource: record.bundleAccess.access.accessSource,
        });
        break;
      default:
        console.log(`Cannot handle key: ${e.key}.`);
        break;
    }
  };

  const queryClient = useQueryClient();

  const { getQuery, getCSVLinkProps, getTableProps, getSearchInputProps } = Tables.useTableState<DataType>(
    'Subscription Access',
    [
      {
        title: 'Action',
        width: 170,
        render: (_, record) => {
          return (
            <Dropdown.Button
              size="small"
              onClick={() => setShowSubscriptionModal({ subscriptionId: record.subscriptionId })}
              menu={{
                items: actions,
                onClick: (e) => handleActionMenuClick(e, record),
              }}
            >
              <Space>
                <EyeOutlined />
                Details
              </Space>
            </Dropdown.Button>
          );
        },
      },
      {
        title: 'Status',
        width: 170,
        dataIndex: 'status',
        ellipsis: true,
        sorter: true,
        render: (status: SubscriptionAccessStatus, record) => (
          <SubscriptionAccessStatusRenderer
            access={record.bundleAccess.access}
            status={status}
            user={record.subscription.user}
          />
        ),
        filters: [
          { text: 'New', value: SubscriptionAccessStatus.NEW },
          { text: 'Queued', value: SubscriptionAccessStatus.QUEUED },
          { text: 'Retry', value: SubscriptionAccessStatus.RETRY },
          { text: 'Pending', value: SubscriptionAccessStatus.PENDING },
          { text: 'Waiting On Dependency', value: SubscriptionAccessStatus.WAITING_ON_DEPENDENCY },
          { text: 'Completed', value: SubscriptionAccessStatus.COMPLETED },
          { text: 'Cancelled', value: SubscriptionAccessStatus.CANCELLED },
          { text: 'Error', value: SubscriptionAccessStatus.ERROR },
          { text: 'Approved', value: SubscriptionAccessStatus.APPROVED },
          { text: 'Rejected', value: SubscriptionAccessStatus.REJECTED },
          { text: 'Expired', value: SubscriptionAccessStatus.EXPIRED },
          { text: 'Cancelled At Source', value: SubscriptionAccessStatus.CANCELLED_AT_SOURCE },
        ],
      },
      {
        title: 'Subscribed',
        width: 140,
        dataIndex: 'subscribed',
        sorter: true,
        render: (subscribed) => <Tables.BooleanRenderer value={subscribed} />,
        filters: [
          { text: 'True', value: true },
          { text: 'False', value: false },
        ],
        filterMultiple: false,
      },
      {
        title: 'Is Eligible',
        width: 150,
        dataIndex: 'isEligible',
        sorter: true,
        render: (isEligible, record) => (
          <Space>
            <StartPullUserSubscriptionAccessEligibilityButton subscriptionAccessId={record.id} />
            <EligibilityRenderer isEligible={isEligible} eligibility={record.eligibility} />
          </Space>
        ),
        filters: [
          { text: 'True', value: true },
          { text: 'False', value: false },
        ],
        filterMultiple: false,
      },
      {
        title: 'Case',
        width: 200,
        render: (_, record) => (
          <CaseRenderer
            userId={record.subscription.userId}
            accessId={record.bundleAccess.accessId}
            onOpenCaseView={(caseId) => setShowCaseModal({ caseId })}
          />
        ),
      },
      {
        title: 'User',
        width: 250,
        dataIndex: ['subscription', 'user', 'name'],
        ellipsis: true,
        sorter: true,
        defaultSortOrder: 'ascend',
        render: (name: string, record) => (
          <UserPopover user={record.subscription.user}>
            <Button type="link" size="small" style={{ padding: 0 }}>
              {name}
            </Button>
          </UserPopover>
        ),
      },
      {
        title: 'Enterprise Id',
        width: 100,
        dataIndex: ['subscription', 'user', 'enterpriseId'],
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Employee Type',
        dataIndex: ['subscription', 'user', 'employeeType'],
        sorter: true,
        width: 170,
        render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
        filters: [
          { text: 'Intel', value: EmployeeType.INTEL },
          { text: 'CW', value: EmployeeType.CONTINGENT_WORKER },
        ],
      },
      {
        title: 'Access',
        width: 350,
        dataIndex: ['bundleAccess', 'access', 'name'],
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Status Description',
        dataIndex: 'statusDescription',
        width: 350,
        ellipsis: true,
      },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 200,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 200,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
    ],
    {
      globalSearchFields: ['bundleAccess.access.name', 'subscription.user.name'],
      disableSession: true,
    },
  );

  const queryResult = useAdvancedFindSubscriptionAccess<DataType>(
    getQuery({
      where: {
        bundleAccess: {
          bundleId,
        },
      },
      include: {
        subscription: {
          include: {
            user: true,
          },
        },
        bundleAccess: {
          include: {
            access: true,
          },
        },
      },
    }),
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: accessCaseKeys.all }); // force repull of cases
    queryResult.refetch();
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <SubscriptionDetailModel
        subscriptionId={showSubscriptionModal?.subscriptionId}
        onClose={() => setShowSubscriptionModal(undefined)}
      />
      <SubscriptionAccessArchiveModal
        subscriptionAccess={showSubscriptionAccessArchiveModal}
        onClose={() => setShowSubscriptionAccessArchiveModal(undefined)}
      />
      <CaseViewModal caseId={showCaseModal?.caseId} onClose={() => setShowCaseModal(undefined)} />
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <Input placeholder="Search..." {...getSearchInputProps()} />
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Refresh
          </Button>
          <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />
        </Space>
      </Space>
      <Table
        {...getTableProps(queryResult)}
        scroll={{
          y: tableHeight,
        }}
      />
    </Space>
  );
};
